/**
 * New Account modal for the Account Management pipeline.
 *
 * Two modes toggled at the top of the modal:
 *   • Manual — a form that captures the 9 survey fields from marketing
 *   • CSV import — drop a Facebook Lead Ads export straight in, headers
 *     are auto-mapped by the backend
 *
 * A successful save calls `onCreated(newAccount)` so the parent Kanban can
 * merge the row into its `new_enquiry` column without a full refetch.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Upload, Users, FileText, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

const SURVEY_FIELDS = [
  { key: 'projects_installed',   label: 'How many resin-bound projects do you currently install?', placeholder: 'e.g. 5-10 a year' },
  { key: 'current_supplier',     label: 'Which supplier are you currently using most?',            placeholder: 'e.g. SureSet, DerbyStone' },
  { key: 'what_matters_most',    label: 'What matters most when choosing a resin supplier?',       placeholder: 'e.g. price, lead-time, colour range' },
  { key: 'current_frustrations', label: 'What are your biggest frustrations with your current supplier?', placeholder: 'Free text' },
  { key: 'interested_in',        label: 'What would you be interested in?',                        placeholder: 'e.g. Sample kit, Trade pricing' },
];

const NewAccountModal = ({ onClose, onCreated, onImported }) => {
  const [mode, setMode] = useState('manual'); // 'manual' | 'csv'
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    projects_installed: '', current_supplier: '', what_matters_most: '',
    current_frustrations: '', interested_in: '', source: 'manual', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitManual = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/accounts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          name: form.name.trim(),
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to save');
      const account = await res.json();
      toast.success(`${account.reference} added`);
      onCreated?.(account);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCsv = async () => {
    if (!csvFile) { toast.error('Pick a CSV first'); return; }
    if (busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const res = await fetch(`${API_URL}/api/staff/accounts/import`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Import failed');
      const data = await res.json();
      const parts = [`Added ${data.created_count}`];
      if (data.skipped_count) parts.push(`skipped ${data.skipped_count} duplicate${data.skipped_count === 1 ? '' : 's'}`);
      toast.success(parts.join(' · '));
      onImported?.(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="new-account-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="font-bold text-lg flex items-center gap-2">
            <Users className="w-5 h-5" /> New Enquiry
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode switcher */}
        <div className="p-3 border-b bg-slate-50 flex gap-2">
          <button
            type="button"
            onClick={() => setMode('manual')}
            data-testid="account-mode-manual"
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition ${
              mode === 'manual' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 hover:bg-slate-100'
            }`}
          >
            <ClipboardList className="w-4 h-4" /> Add one
          </button>
          <button
            type="button"
            onClick={() => setMode('csv')}
            data-testid="account-mode-csv"
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition ${
              mode === 'csv' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 hover:bg-slate-100'
            }`}
          >
            <Upload className="w-4 h-4" /> Import CSV
          </button>
        </div>

        {mode === 'manual' ? (
          <form onSubmit={submitManual} className="p-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="acc-name">Name <span className="text-rose-600">*</span></Label>
                <Input id="acc-name" data-testid="acc-name" value={form.name} onChange={setField('name')} required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="acc-phone">Phone</Label>
                <Input id="acc-phone" data-testid="acc-phone" value={form.phone} onChange={setField('phone')} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="acc-email">Email</Label>
                <Input id="acc-email" data-testid="acc-email" type="email" value={form.email} onChange={setField('email')} className="mt-1" />
              </div>
            </div>

            <div className="pt-2 border-t space-y-3">
              <div className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Marketing survey
              </div>
              {SURVEY_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label htmlFor={`acc-${f.key}`}>{f.label}</Label>
                  <Textarea
                    id={`acc-${f.key}`}
                    data-testid={`acc-${f.key}`}
                    rows={2}
                    value={form[f.key]}
                    onChange={setField(f.key)}
                    placeholder={f.placeholder}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="acc-submit" className="bg-slate-900 hover:bg-slate-800">
                {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Add Enquiry'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-3">
            <p className="text-sm text-slate-600">
              Drop in a CSV export from Facebook Lead Ads (or any spreadsheet). Columns are auto-detected — as long as
              a <span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">Name</span> column exists, the rest map by header name.
            </p>
            <p className="text-xs text-slate-500">
              Duplicates are auto-skipped based on phone or email so you can re-run the same export safely.
            </p>
            <label className="block border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:bg-slate-50" data-testid="acc-csv-drop">
              <Upload className="w-6 h-6 mx-auto mb-2 text-slate-500" />
              <div className="text-sm font-semibold">{csvFile ? csvFile.name : 'Click to select a CSV'}</div>
              {csvFile && (
                <div className="text-xs text-slate-500 mt-1">{(csvFile.size / 1024).toFixed(1)} KB</div>
              )}
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                data-testid="acc-csv-input"
              />
            </label>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button
                type="button"
                onClick={submitCsv}
                disabled={busy || !csvFile}
                data-testid="acc-csv-submit"
                className="bg-slate-900 hover:bg-slate-800"
              >
                {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importing…</> : 'Import'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewAccountModal;
